import React from 'react'
import { css } from '@emotion/core'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from 'recharts'
import { ChoiceDistribution } from '@/models/survey/statistics'
import { useTranslation } from 'react-i18next'
import { MultilingualText } from '@/models/survey/types'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'

/**
 * 棒グラフカードコンポーネント
 * 選択式質問の回答分布を棒グラフで表示
 */
type Props = {
  title: MultilingualText
  distribution: ChoiceDistribution
}

export const BarChartCard = ({ title, distribution }: Props) => {
  const { i18n } = useTranslation()
  const langDistribution = distribution[i18n.language] || distribution['ja'] || {}
  const pickByLanguage = usePickByLanguage()

  // 色の配列（percentageが多い順に適用）
  const colors = ['#0017C1', '#4C5DD3', '#7F8AE0', '#B2B9EC', '#E5E7F9']

  // 選択肢データをパーセンテージの降順でソートし、色を割り当て
  const data = Object.entries(langDistribution)
    .map(([choice, data]: [string, { percentage: number; count: number }]) => ({
      name: choice,
      percentage: data.percentage,
      count: data.count,
    }))
    .sort((a, b) => b.percentage - a.percentage)
    .map((item, index) => ({
      ...item,
      fill: colors[Math.min(index, colors.length - 1)], // 色配列の範囲内で色を割り当て
    }))

  return (
    <div css={containerStyle}>
      <div css={titleContainerStyle}>{pickByLanguage(title)}</div>
      <ResponsiveContainer width="100%" height="90%">
        <BarChart data={data} layout="vertical" margin={{ top: 40, right: 40, bottom: 20 }} barCategoryGap={8}>
          <XAxis type="number" tick={false} axisLine={false} tickLine={false} />
          <YAxis
            dataKey="name"
            type="category"
            width={100}
            tickLine={false}
            tick={props => {
              const { x, y, payload } = props
              const text = payload.value
              const truncatedText = text.length > 10 ? text.substring(0, 10) + '...' : text
              return (
                <text x={x - 90} y={y} textAnchor="start" fill="#676767" fontSize="12px" dy={5}>
                  {truncatedText}
                </text>
              )
            }}
          />
          <Tooltip />
          <Bar dataKey="percentage" barSize={25}>
            <LabelList fill="#676767" fontSize="12px" dataKey="percentage" position="right" formatter={(value: number) => `${value}%`} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

const containerStyle = css({
  width: '100%',
  height: '400px',
  padding: '16px',
  background: '#fff',
  borderRadius: '4px',
  border: '1px solid #D9D9D9',
  marginBottom: '24px',
})

const titleContainerStyle = css({
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#272727',
  textAlign: 'center',
  justifyContent: 'center',
  borderBottom: '1px solid #F2F2F2',
  marginLeft: '-16px',
  marginRight: '-16px',
  paddingBottom: '16px',
})
